// services/navigation.service.ts
import { Injectable } from '@angular/core';
import { Router, NavigationEnd } from '@angular/router';
import { filter } from 'rxjs/operators';
import { AuthService } from '../auth/auth.service';
import { GeneralesService } from './general.service';
import { LocalStorageService } from 'angular-web-storage';
import { LicenciaAlertService } from './licenciaAlerte.service';

export interface MenuItem {
    path: string;
    title: string;
    icon: string;
    classs: string;
    badge: string;
    badgeClass: string;
    isExternalLink: boolean;
    submenu: MenuItem[];
}

@Injectable({
    providedIn: 'root'
})
export class NavigationService {
    private currentUrl: string = '';
    private previousUrl: string = '';
    private menuItems: MenuItem[] = [];

    constructor(
        private router: Router,
        private authService: AuthService,
        private generalesService: GeneralesService,
        public local: LocalStorageService,
        private licenciaAlertService: LicenciaAlertService
    ) {
        this.trackNavigation();
        this.loadMenuItems();
    }

    private trackNavigation() {
        this.router.events.pipe(
            filter(event => event instanceof NavigationEnd)
        ).subscribe((event: NavigationEnd) => {
            this.previousUrl = this.currentUrl;
            this.currentUrl = event.urlAfterRedirects;
            console.log('URL actual:', this.currentUrl); // Para debug

            // Define las reglas de negocio
            const urlConfigs = [
                {
                    urls: ['/'],
                    state: { sePuedeTimbrar: false, esComercial: false, esContable: false }
                },
                {
                    urls: ['/ventas-ingresos/busquedaDocumentosVentas', '/cuentas-por-cobrar/cobranza'],
                    state: { sePuedeTimbrar: true, esComercial: false, esContable: false }
                },
                {
                    urls: ['/ventas-ingresos/documentos-ventas', '/compras-egresos/documentos-compras', '/inventarios/movimientos-almacen'],
                    state: { sePuedeTimbrar: false, esComercial: true, esContable: false }
                },
                {
                    urls: ['/contabilidad/documento-contable'],
                    state: { sePuedeTimbrar: false, esComercial: false, esContable: true }
                }
            ];

            // Estado por defecto (mismo que '/')
            const defaultState = { sePuedeTimbrar: false, esComercial: false, esContable: false };

            // Busca la configuración que coincide o usa el default
            const matchingConfig = urlConfigs.find(config => config.urls.includes(this.currentUrl));
            const newState = matchingConfig ? matchingConfig.state : defaultState;

            // Aplica el estado
            Object.assign(this.authService.usuarioConectado, newState);

            // Aqui revisar la parte de la informacion de las empresa

            const IdEmpresa = this.authService.usuarioConectado.Empresa.IdEmpresa;
            if (!IdEmpresa) return;

            this.generalesService.obtenerEmpresaInformacionLicencia(IdEmpresa).subscribe(respuestaEmpresa => {

                if (respuestaEmpresa != null && respuestaEmpresa.code == 200) {

                    console.log('Aqui llega cuando ya consulto info de empresa...');

                    const usuarioStorage = this.local.get('UsuarioCompleto_' + IdEmpresa);

                    if (usuarioStorage) {
                        //this.authService.usuarioConectado = JSON.parse(usuarioStorage);

                        this.authService.usuarioConectado.Empresa.FechaLicenciaVigencia =
                            respuestaEmpresa.model.FechaLicenciaVigencia;
                        this.authService.usuarioConectado.Empresa.DiasRestantesLicencia =
                            respuestaEmpresa.model.DiasRestantesLicencia;
                        this.authService.usuarioConectado.Empresa.TimbresLicenciaRestantes =
                            respuestaEmpresa.model.TimbresLicenciaRestantes;
                        this.authService.usuarioConectado.TipoLicencia =
                            respuestaEmpresa.model.TipoLicencia;

                        this.local.set(
                            'UsuarioCompleto_' + this.authService.usuarioConectado.Empresa.IdEmpresa,
                            JSON.stringify(this.authService.usuarioConectado)
                        );
                    }

                    // 👇 Notificamos al componente (desacoplado)
                    this.licenciaAlertService.notificarEmpresaActualizada({
                        FechaLicenciaVigencia: this.authService.usuarioConectado.Empresa.FechaLicenciaVigencia,
                        TipoLicencia: this.authService.usuarioConectado.TipoLicencia
                    });
                    this.licenciaAlertService.dispararRefresco();
                }
            });

        });
    }

    private loadMenuItems() {
        // Cargar items del menú desde authService
        if (this.authService.usuarioConectado?.MenuItems) {
            this.menuItems = this.authService.usuarioConectado.MenuItems;
        }
    }

    // Obtener URL actual
    getCurrentUrl(): string {
        return this.currentUrl;
    }

    // Obtener URL anterior
    getPreviousUrl(): string {
        return this.previousUrl;
    }

    // Validar si la URL actual coincide exactamente
    isCurrentUrl(url: string): boolean {
        return this.currentUrl === url;
    }

    // Validar si estamos en una sección específica del menú
    isInSection(sectionPath: string): boolean {
        return this.currentUrl.startsWith(sectionPath);
    }

    // Buscar un item del menú por su URL (recursivo)
    findMenuItemByUrl(url: string, items: MenuItem[] = this.menuItems): MenuItem | null {
        for (const item of items) {
            // Si el item tiene path y coincide con la URL
            if (item.path && item.path === url) {
                return item;
            }

            // Buscar en submenús
            if (item.submenu && item.submenu.length > 0) {
                const found = this.findMenuItemByUrl(url, item.submenu);
                if (found) return found;
            }
        }
        return null;
    }

    // Obtener el título del menú para una URL específica
    getMenuTitleByUrl(url: string): string {
        const menuItem = this.findMenuItemByUrl(url);
        return menuItem ? menuItem.title : '';
    }

    // Obtener la ruta completa del breadcrumb para una URL
    getBreadcrumb(url: string): { title: string, path: string }[] {
        const breadcrumb: { title: string, path: string }[] = [];
        this.buildBreadcrumb(url, this.menuItems, breadcrumb);
        return breadcrumb;
    }

    private buildBreadcrumb(
        url: string,
        items: MenuItem[],
        breadcrumb: { title: string, path: string }[]
    ): boolean {
        for (const item of items) {
            if (item.path && item.path === url) {
                breadcrumb.push({ title: item.title, path: item.path });
                return true;
            }

            if (item.submenu && item.submenu.length > 0) {
                if (this.buildBreadcrumb(url, item.submenu, breadcrumb)) {
                    // Agregar el padre al breadcrumb si encontramos el hijo
                    if (item.path) {
                        breadcrumb.unshift({ title: item.title, path: item.path });
                    } else {
                        // Si el padre no tiene path, solo agregamos el título
                        breadcrumb.unshift({ title: item.title, path: '' });
                    }
                    return true;
                }
            }
        }
        return false;
    }

    // Obtener todos los items del menú (útil para búsquedas)
    getAllMenuItems(): MenuItem[] {
        return this.menuItems;
    }

    // Verificar si una URL existe en el menú
    isValidRoute(url: string): boolean {
        return this.findMenuItemByUrl(url) !== null;
    }

    // Obtener el padre de un item del menú
    getParentItem(url: string): MenuItem | null {
        for (const item of this.menuItems) {
            if (item.submenu && item.submenu.length > 0) {
                const found = item.submenu.find(sub => sub.path === url);
                if (found) return item;

                // Buscar en submenús más profundos
                for (const sub of item.submenu) {
                    if (sub.submenu && sub.submenu.length > 0) {
                        const deepFound = sub.submenu.find(deep => deep.path === url);
                        if (deepFound) return sub;
                    }
                }
            }
        }
        return null;
    }

    // Actualizar menú items (llamar cuando cambie el usuario)
    refreshMenuItems() {
        this.loadMenuItems();
    }
}